import get from 'lodash.get';
import values from 'lodash.values';
import flatten from 'lodash.flatten';
import {
    CS_MEASURES,
    MEASURE_BY_MEASURE_SCORE,
    SHORT_GENRE_TYPES,
    ASSESSMENT_TYPE,
    MEASURE_NAMES,
    SKILL_NAMES,
} from '../../constants/assessments';
import { GRADE, PERIOD, GRADE_LABEL_SHORT } from '../../constants/enrollment';
import { FORMS_NAMES_HISTORY_MAP } from '../../constants/forms';
import { getMeasureScoreBySkillName } from './measureScoreBySkillName';

/**
 * Get form list by measure score name
 * @param {Array} forms - array of PM period
 * @param {string} measureScoreName - measure score name
 * @returns {Array} list of forms with specific measure:
 *  [
 *      {
 *          formId: "...",
 *          period: "...",
 *          grade: "...",
 *          measure: "...",
 *          sequence: 1,
 *      }
 *      ...
 *  ]
 */
export const getFormsByMeasure = (forms, measureScoreName) =>
    forms.filter(form => form.measure === MEASURE_BY_MEASURE_SCORE[measureScoreName]);

/**
 * Return grouped forms by grade with filtered empty data
 * @returns {Object}
 * {
 *      'KG': [{ formId: ...}, ...],
 *      '01': [{ formId: ...}, ...],
 *      ...
 * }
 */
export const getGroupedForms = (rawForms, skillName, measureName) =>
    getFormsByMeasure(rawForms, getMeasureScoreBySkillName(skillName, measureName)).reduce(
        (acc, form) => ({
            ...acc,
            [form.grade]: [...get(acc, form.grade, []), form],
        }),
        {},
    );

/**
 * Getting sorted list of grades from list of grades
 * @param grades - List of grades
 * @returns {Array} - ASC sorting with placing KG grade on first position
 * Example: ['KG', '01',...]
 */
export const sortGrades = grades =>
    grades.sort((a, b) => {
        if (a === GRADE.KG) {
            return -1;
        }
        if (b === GRADE.KG) {
            return 1;
        }
        return a - b;
    });

/**
 * Getting sorted list of grades from available assessment forms
 * @param forms - Object of assessment forms
 * @returns {Array} - ASC sorting with placing KG grade on first position
 * Example: ['KG', '01',...]
 */
export const getSortedGrades = forms => sortGrades(Object.keys(forms));

export const getSortedForms = forms => forms.sort((a, b) => a.sequence - b.sequence);

const getFormLabel = form =>
    form.period === PERIOD.PM
        ? // TODO: Remove FORM_NAMES_MAP when ORF PDF form would be printed
          `${GRADE_LABEL_SHORT[form.grade]}, ${get(
              FORMS_NAMES_HISTORY_MAP,
              form.uid,
              `F${form.sequence}`,
          )}`
        : form.period;

/**
 * Calculate form number. Burst has all seq started from 0
 * CS probe seq is 1 and 3 for F, 2 and 4 for NF
 * Regular seq is incremented but CS divided by 2 and incremented.
 * @param form - form object
 * @returns {number} form number
 */
export const getBurstSequence = form =>
    CS_MEASURES.includes(form.measure) ? Math.floor(form.seq / 2) + 1 : form.seq + 1;

/**
 * Generate suffix for burst measure form in format: cs:`1, F` or just `1` ...
 * WIll be used in several places
 * @param form - data
 * @returns {string} - suffix
 */
export const getBurstFormSuffix = form =>
    `${getBurstSequence(form)}${form.genre ? `, ${SHORT_GENRE_TYPES[form.genre]}` : ``}`;

/**
 * Generate label for measure drop down on PM tab.
 * @param form
 * @returns {string}
 */
const getBurstFormLabel = form =>
    form.period === PERIOD.PM
        ? `${
              form.measure === MEASURE_NAMES.VOC ? `${GRADE_LABEL_SHORT[form.grade]}, ` : ``
          }F${getBurstFormSuffix(form)}`
        : form.period;

/**
 * Construct look up map for common forms data by form id.
 * BM form name: <BM period>
 * PM form name: <form grade>, <form sequence>
 * @param {Object} forms map by period: {<period>: [<forms]}
 * @returns {Object} map labels by forms id: {<form id>: <label>}
 */
export const createFormsMap = forms =>
    flatten(values(forms)).reduce(
        (formsMap, form) => ({
            ...formsMap,
            [form.uid]: {
                period: form.period,
                grade: form.grade,
                measure: form.measure,
                uid: form.uid,
                sequence: get(form, `sequence`, 0),
                label: getFormLabel(form),
            },
        }),
        {},
    );

/**
 * format burst form to be dibels like with label, uid, sequence
 * @param {Object} form single form object
 * @returns {Object} formatted form object
 */
export const formatBurstForm = form => ({
    period: form.period,
    grade: form.grade,
    measure: form.measure,
    uid: form.probeId,
    probeId: form.probeId,
    genre: form.genre,
    is_alt: form.is_alt,
    sequence: form.seq,
    label: getBurstFormLabel(form),
});

/**
 * Construct look up map for burst forms data by probe id.
 * BM form name: <BM period>
 * PM form name: <form grade>, <form sequence>
 * @param {Object} forms map by period: {<period>: [<forms]}
 * @returns {Object} map labels by forms id: {<form id>: <label>}
 */
export const createBurstFormsMap = forms =>
    flatten(values(forms)).reduce(
        (formsMap, form) => ({
            ...formsMap,
            [form.probeId]: formatBurstForm(form),
        }),
        {},
    );

/**
 * Construct look up map for CLPL forms data by form id.
 * BM form name: <BM period>
 * PM form name: <form grade>, <form sequence>
 * @param {Object} forms map by period: {<period>: [<forms]}
 * @returns {Object} map labels by forms id: {<form id>: <label>}
 */
export const createCLPLFormsMap = forms =>
    flatten(values(forms)).reduce(
        (formsMap, form) => ({
            ...formsMap,
            [form.formId]: {
                period: form.period,
                grade: form.grade,
                measure: form.measure,
                formId: form.formId,
                label: getFormLabel(form),
            },
        }),
        {},
    );

/**
 * Get raw forms
 * @param {Object} forms - Object of assessment forms
 * @returns {Array}
 */
export const getRawForms = forms =>
    flatten(values(forms).map(appForms => appForms[ASSESSMENT_TYPE.PM])).filter(
        form => form !== undefined,
    );

/**
 * Check forms by grade
 * @param {String} grade - student grade
 * @param {String} skillName - measure skill name
 * @param {Object} formsBySkill - forms map by grade
 * @returns {Array}
 */
export const getFormByGradeAndSkillName = (grade, skillName, formsBySkill) =>
    (grade === GRADE.TWO && skillName === SKILL_NAMES.PHONEMIC_AWARENESS
        ? formsBySkill[GRADE.ONE]
        : formsBySkill[grade]) || [];
